<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="sel-popover"
      :style="{ top: pos.y + 'px', left: pos.x + 'px' }"
      @mousedown.prevent.stop
    >
      <div class="sel-popover__arrow"></div>
      <div class="sel-popover__inner">
        <span class="sel-popover__preview">„{{ preview }}"</span>
        <button class="sel-popover__btn" @mousedown.prevent.stop="askAbout">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          Frag FormIQ
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';

export default {
  name: 'SelectionPopover',
  setup() {
    const visible = ref(false);
    const pos = ref({ x: 0, y: 0 });
    const selectedText = ref('');

    const preview = computed(() => {
      const t = selectedText.value;
      return t.length > 40 ? t.slice(0, 40) + '…' : t;
    });

    const hide = () => {
      visible.value = false;
      selectedText.value = '';
    };

    const onMouseUp = (e) => {
      // Don't trigger inside the chat window itself
      if (e.target?.closest?.('.chat-window') || e.target?.closest?.('.chat-fab')) {
        return;
      }
      setTimeout(() => {
        const sel = window.getSelection();
        const text = sel?.toString().trim();
        if (!text || text.length < 3) {
          hide();
          return;
        }
        selectedText.value = text;
        // Position popover below the end of the selection
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const x = Math.max(8, Math.min(
          rect.left + rect.width / 2 - 100,
          window.innerWidth - 220
        ));
        pos.value = {
          x: x + window.scrollX,
          y: rect.bottom + window.scrollY + 10,
        };
        visible.value = true;
      }, 20);
    };

    const onMouseDown = (e) => {
      if (e.target?.closest?.('.sel-popover')) return;
      hide();
    };

    const onSelectionChange = () => {
      const text = window.getSelection()?.toString().trim();
      if (!text) hide();
    };

    const askAbout = () => {
      if (!selectedText.value) return;
      const truncated = selectedText.value.length > 150
        ? selectedText.value.slice(0, 150) + '…'
        : selectedText.value;
      window.dispatchEvent(new CustomEvent('formpilot:ask-about', {
        detail: { text: `Was bedeutet „${truncated}"?` },
      }));
      hide();
      // Clear the text selection
      window.getSelection()?.removeAllRanges();
    };

    onMounted(() => {
      document.addEventListener('mouseup', onMouseUp);
      document.addEventListener('mousedown', onMouseDown);
      document.addEventListener('selectionchange', onSelectionChange);
    });

    onUnmounted(() => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('selectionchange', onSelectionChange);
    });

    return { visible, pos, preview, askAbout };
  },
};
</script>

<style scoped>
.sel-popover {
  position: absolute;
  z-index: 3000;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.18));
  pointer-events: all;
}

.sel-popover__arrow {
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: 7px solid #003366;
  margin-left: 93px; /* roughly center over button */
}

.sel-popover__inner {
  background: #003366;
  border-radius: 6px;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 200px;
  max-width: 280px;
}

.sel-popover__preview {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-style: italic;
  line-height: 1.3;
  padding: 0 2px;
}

.sel-popover__btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;
  width: 100%;
  justify-content: center;
}

.sel-popover__btn:hover {
  background: rgba(255, 255, 255, 0.22);
}
</style>
